// Importing widget.css triggers the css-injected-by-js plugin to capture all
// CSS (this file + Vue SFC styles + transitive node_modules styles) into
// window.__AI_WIDGET_CSS__. We consume that global inside mountWidget().
import './styles/widget.css';
import { createApp } from 'vue';
import { applyVariant } from './lib/variants.js';
import { startVariantPropagation } from './lib/variant-propagation.js';
// Active design variant (swap this import to ship a different design).
// Available alternatives sit under ./designs/* and are exercised via Storybook:
//   ./designs/01-pill-composer/...     — pill composer + bottom drawer
//   ./designs/02-side-panel/Design02   — right-edge grabber + side panel
//   ./designs/03-inline-hero/Design03  — inline hero input + centered modal
//   ./designs/04-sticky-dock/Design04  — sticky bottom dock + peek pill  (← active)
import App from './designs/02-side-panel/Design02.vue';

// Minimal pub/sub for the AIWidget.on(event, cb) public surface.
function createEventBus() {
    const handlers = new Map();
    return {
        on(event, cb) {
            if (!handlers.has(event)) handlers.set(event, new Set());
            handlers.get(event).add(cb);
            return () => handlers.get(event)?.delete(cb);
        },
        off(event, cb) {
            handlers.get(event)?.delete(cb);
        },
        emit(event, payload) {
            handlers.get(event)?.forEach((cb) => {
                try { cb(payload); } catch (e) { console.error('[AIWidget] handler error', e); }
            });
        },
    };
}

/**
 * Mount the active Vue design into a top-level host element on the page.
 *
 * NOTE: an earlier iteration used Shadow DOM for full style isolation, but
 * vaul-vue (Design 01's drawer) tightly couples to document.body — it injects
 * its own CSS to document.head, manipulates body scroll, and portals via
 * Vue Teleport. So we mount in normal DOM and scope tokens with `.aia-widget`.
 * Adding Shadow DOM back is a future hardening step for cross-origin hosts.
 */
function mountWidget({ component, props = {}, css = '', hostId = 'ai-assistant-widget', stylesId = 'ai-assistant-widget-styles' }) {
    // Inject CSS once at document level. Tokens are scoped to `.aia-widget`
    // in widget.css so they don't leak to the host page.
    if (css) {
        let styleEl = document.getElementById(stylesId);
        if (!styleEl) {
            styleEl = document.createElement('style');
            styleEl.id = stylesId;
            document.head.appendChild(styleEl);
        }
        styleEl.textContent = css;
    }

    // Reuse host if already mounted (idempotent init).
    let host = document.getElementById(hostId);
    if (!host) {
        host = document.createElement('div');
        host.id = hostId;
        host.className = 'aia-widget';
        document.body.appendChild(host);
    } else {
        host.innerHTML = '';
        host.className = 'aia-widget';
    }

    const app = createApp(component, props);
    app.mount(host);

    return { app, host };
}

// Module-level state — one widget per page is the supported pattern.
let mounted = null;
let stopVariantPropagation = null;
const events = createEventBus();

function validateConfig(config) {
    if (!config || typeof config !== 'object') {
        throw new Error('[AIWidget] init() requires a config object.');
    }
    if (!config.endpoint || typeof config.endpoint !== 'string') {
        throw new Error('[AIWidget] config.endpoint is required (URL or path to the chat API).');
    }
    if (!config.apiKey || typeof config.apiKey !== 'string') {
        throw new Error('[AIWidget] config.apiKey is required (public key for widget auth).');
    }
}

function applyThemeOverrides(host, theme) {
    if (!theme || typeof theme !== 'object') return;
    const map = {
        primary: '--primary',
        background: '--background',
        foreground: '--foreground',
        accent: '--accent',
        border: '--border',
        radius: '--radius',
    };
    for (const [key, cssVar] of Object.entries(map)) {
        if (theme[key]) host.style.setProperty(cssVar, theme[key]);
    }
}

const AIWidget = {
    init(config = {}) {
        validateConfig(config);

        // If already mounted, treat as a re-init (config change).
        if (mounted) {
            this.destroy();
        }

        const css = (typeof window !== 'undefined' && window.__AI_WIDGET_CSS__) || '';
        mounted = mountWidget({
            component: App,
            props: { config, events },
            css,
        });

        applyVariant(mounted.host, config.variant);
        stopVariantPropagation = startVariantPropagation(mounted.host);
        applyThemeOverrides(mounted.host, config.theme);
        events.emit('ready', { config });
        return AIWidget;
    },

    open() {
        events.emit('request-open');
        return AIWidget;
    },

    close() {
        events.emit('request-close');
        return AIWidget;
    },

    destroy() {
        if (!mounted) return;
        try { stopVariantPropagation?.(); } catch (_) { /* ignore */ }
        stopVariantPropagation = null;
        try { mounted.app.unmount(); } catch (_) { /* ignore */ }
        mounted.host.remove();
        mounted = null;
        events.emit('destroyed');
    },

    on(event, cb) {
        events.on(event, cb);
        return AIWidget;
    },
};

if (typeof window !== 'undefined') {
    window.AIWidget = AIWidget;
}

export default AIWidget;
